<script lang="ts">
    import { onMount } from 'svelte';
    import { fetchHistory, fetchStats, type AnalysisHistoryItem, type AnalysisStats } from '../../api/history';
    import {
        fetchClosedPositions,
        type ClosedPosition,
        type ClosedPositionPeriod,
        type ClosedPositionsSummary,
    } from '../../api/futures';
    import { fetchLastMistakeReport, runMistakeAnalysis, type MistakeReport, type MistakePeriod } from '../../api/mistakes';

    let stats   = $state<AnalysisStats | null>(null);
    let history = $state<AnalysisHistoryItem[]>([]);
    let loading = $state(true);
    let error   = $state('');
    let filterSymbol = $state('');

    // Таби
    let activeTab = $state<'analyses' | 'positions' | 'mistakes'>('analyses');

    // Позиції
    let posLoading  = $state(false);
    let posError    = $state('');
    let period      = $state<ClosedPositionPeriod>('30d');
    let posSymbol   = $state('');
    let positions   = $state<ClosedPosition[]>([]);
    let posSummary  = $state<ClosedPositionsSummary | null>(null);
    let posSymbolFilter = $state('');   // для фільтру таблиці на клієнті

    // Аналіз помилок
    let mistakePeriod   = $state<MistakePeriod>('30d');
    let mistakeReport   = $state<MistakeReport | null>(null);
    let mistakeLoading  = $state(false);
    let mistakeError    = $state('');

    async function loadLastMistakeReport() {
        try {
            const r = await fetchLastMistakeReport(mistakePeriod);
            if (r.success) mistakeReport = r;
        } catch { /* no report yet */ }
    }

    async function startMistakeAnalysis() {
        mistakeLoading = true;
        mistakeError   = '';
        try {
            const r = await runMistakeAnalysis(mistakePeriod);
            if (r.success) mistakeReport = r;
            else mistakeError = r.error ?? 'Помилка аналізу';
        } catch (e: any) {
            mistakeError = e.message ?? 'Помилка аналізу';
        } finally {
            mistakeLoading = false;
        }
    }

    onMount(async () => {
        try {
            [stats, history] = await Promise.all([fetchStats(), fetchHistory(100)]);
        } catch (e) {
            error = 'Не вдалося завантажити дані. Можливо, потрібна авторизація.';
        } finally {
            loading = false;
        }
    });

    async function applyFilter() {
        loading = true;
        try {
            history = await fetchHistory(100, filterSymbol || undefined);
        } finally {
            loading = false;
        }
    }

    async function loadPositions() {
        posLoading = true;
        posError   = '';
        try {
            const res = await fetchClosedPositions(period, posSymbol || undefined);
            positions  = res.positions;
            posSummary = res.summary;
        } catch {
            posError = 'Не вдалося завантажити. Перевірте API ключі Binance.';
        } finally {
            posLoading = false;
        }
    }

    function switchTab(tab: 'analyses' | 'positions' | 'mistakes') {
        activeTab = tab;
        if (tab === 'positions' && positions.length === 0 && !posLoading) {
            loadPositions();
        }
        if (tab === 'mistakes' && !mistakeReport && !mistakeLoading) {
            loadLastMistakeReport();
        }
    }

    let filteredPositions = $derived(
        posSymbolFilter
            ? positions.filter(p => p.symbol.includes(posSymbolFilter.toUpperCase()))
            : positions
    );

    // helpers
    function dirColor(d: string) {
        if (d === 'long')  return '#2e7d32';
        if (d === 'short') return '#c62828';
        return '#546e7a';
    }
    function dirLabel(d: string) {
        if (d === 'long')  return 'LONG ▲';
        if (d === 'short') return 'SHORT ▼';
        return 'ОЧІКУВАТИ';
    }
    function confColor(c: number) {
        if (c >= 70) return '#2e7d32';
        if (c >= 50) return '#f57f17';
        return '#c62828';
    }
    function pnlColor(v: number) {
        return v > 0 ? '#2e7d32' : v < 0 ? '#c62828' : '#546e7a';
    }
    function fmtDate(iso: string) {
        const d = new Date(iso);
        return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' })
             + ' ' + d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    }
    function fmtTs(ms: number) {
        const d = new Date(ms);
        return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' })
             + ' ' + d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    }
    function pct(count: number, total: number) {
        return total ? Math.round(count / total * 100) : 0;
    }
    function fmtPnl(v: number) {
        return (v >= 0 ? '+' : '') + v.toFixed(2);
    }
</script>

<div class="st-wrap">

    <!-- Таби -->
    <div class="st-tabs">
        <button class="st-tab" class:st-tab--active={activeTab === 'analyses'} onclick={() => switchTab('analyses')}>
            Аналізи AI
        </button>
        <button class="st-tab" class:st-tab--active={activeTab === 'positions'} onclick={() => switchTab('positions')}>
            Позиції Binance
        </button>
        <button class="st-tab" class:st-tab--active={activeTab === 'mistakes'} onclick={() => switchTab('mistakes')}>
            Аналіз помилок
        </button>
    </div>

    <!-- ══════════ ТАБ: АНАЛІЗИ ══════════ -->
    {#if activeTab === 'analyses'}
        {#if loading}
            <div class="text-muted">Завантаження...</div>
        {:else if error}
            <div class="alert alert-warning">{error}</div>
        {:else}

            {#if stats}
                <div class="st-cards">
                    <div class="st-card">
                        <div class="st-card-val">{stats.total}</div>
                        <div class="st-card-lbl">Всього аналізів</div>
                    </div>
                    <div class="st-card" style="border-color:#2e7d32">
                        <div class="st-card-val" style="color:#2e7d32">
                            {stats.by_direction['long'] ?? 0}
                            <span class="st-card-pct">({pct(stats.by_direction['long'] ?? 0, stats.total)}%)</span>
                        </div>
                        <div class="st-card-lbl">LONG</div>
                    </div>
                    <div class="st-card" style="border-color:#c62828">
                        <div class="st-card-val" style="color:#c62828">
                            {stats.by_direction['short'] ?? 0}
                            <span class="st-card-pct">({pct(stats.by_direction['short'] ?? 0, stats.total)}%)</span>
                        </div>
                        <div class="st-card-lbl">SHORT</div>
                    </div>
                    <div class="st-card" style="border-color:#546e7a">
                        <div class="st-card-val" style="color:#546e7a">
                            {stats.by_direction['wait'] ?? 0}
                            <span class="st-card-pct">({pct(stats.by_direction['wait'] ?? 0, stats.total)}%)</span>
                        </div>
                        <div class="st-card-lbl">ОЧІКУВАТИ</div>
                    </div>
                    <div class="st-card">
                        <div class="st-card-val" style="color:{confColor(stats.avg_confidence)}">{stats.avg_confidence}%</div>
                        <div class="st-card-lbl">Сер. впевненість</div>
                    </div>
                </div>

                {#if Object.keys(stats.by_symbol).length > 0}
                    <div class="st-section-title">Топ монети</div>
                    <div class="st-symbols">
                        {#each Object.entries(stats.by_symbol) as [sym, cnt]}
                            <button
                                class="st-sym-chip"
                                class:active={filterSymbol === sym}
                                onclick={() => { filterSymbol = filterSymbol === sym ? '' : sym; applyFilter(); }}
                            >
                                {sym} <span class="st-sym-cnt">{cnt}</span>
                            </button>
                        {/each}
                    </div>
                {/if}
            {/if}

            <div class="st-filter">
                <div class="st-section-title">Історія аналізів</div>
                <div class="d-flex gap-2 align-items-center">
                    <input
                        class="form-control form-control-sm"
                        style="width:140px"
                        placeholder="Символ (напр. BTCUSDT)"
                        bind:value={filterSymbol}
                        onkeydown={(e) => e.key === 'Enter' && applyFilter()}
                    />
                    <button class="btn btn-sm btn-outline-secondary" onclick={applyFilter}>Фільтр</button>
                    {#if filterSymbol}
                        <button class="btn btn-sm btn-link p-0" onclick={() => { filterSymbol = ''; applyFilter(); }}>Скинути</button>
                    {/if}
                </div>
            </div>

            {#if history.length === 0}
                <p class="text-muted mt-2">Аналізів ще немає. Натисніть "✦ AI" на графіку, щоб розпочати.</p>
            {:else}
                <div class="table-responsive">
                    <table class="table table-sm table-hover st-table">
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th>Монета</th>
                                <th>ТФ</th>
                                <th>Напрямок</th>
                                <th>Впевн.</th>
                                <th>Вхід</th>
                                <th>SL</th>
                                <th>TP1</th>
                                <th>Плече</th>
                                <th>AI</th>
                                <th>Коментар</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each history as item}
                                <tr>
                                    <td class="st-date">{fmtDate(item.created_at)}</td>
                                    <td><strong>{item.symbol}</strong></td>
                                    <td class="text-muted">{item.interval}</td>
                                    <td>
                                        <span style="color:{dirColor(item.direction)};font-weight:700;font-size:0.78rem">
                                            {dirLabel(item.direction)}
                                        </span>
                                    </td>
                                    <td>
                                        <span style="color:{confColor(item.confidence)};font-weight:600">
                                            {item.confidence}%
                                        </span>
                                    </td>
                                    <td>{item.trade?.entry?.toLocaleString() ?? '—'}</td>
                                    <td class="text-danger">{item.trade?.sl?.toLocaleString() ?? '—'}</td>
                                    <td class="text-success">{item.trade?.tp1?.toLocaleString() ?? '—'}</td>
                                    <td>{item.trade?.leverage ? `${item.trade.leverage}x` : '—'}</td>
                                    <td>
                                        <span class="st-provider" class:gemini={item.ai_provider === 'gemini'}>
                                            {item.ai_provider === 'gemini' ? 'Gemini' : 'Claude'}
                                        </span>
                                    </td>
                                    <td class="st-summary">{item.summary}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        {/if}
    {/if}

    <!-- ══════════ ТАБ: ПОЗИЦІЇ ══════════ -->
    {#if activeTab === 'positions'}
        <!-- Контролі: період + символ -->
        <div class="pos-controls">
            <div class="pos-periods">
                {#each (['7d','30d','90d','180d'] as ClosedPositionPeriod[]) as p}
                    <button
                        class="pos-period-btn"
                        class:pos-period-btn--active={period === p}
                        onclick={() => { period = p; loadPositions(); }}
                    >{p}</button>
                {/each}
            </div>
            <div class="pos-symbol-row">
                <input
                    class="pos-symbol-input"
                    type="text"
                    placeholder="Символ (напр. BTCUSDT)"
                    bind:value={posSymbol}
                    onkeydown={(e) => e.key === 'Enter' && loadPositions()}
                />
                <button class="pos-reload-btn" onclick={loadPositions} disabled={posLoading}>
                    {posLoading ? '...' : '↻ Оновити'}
                </button>
            </div>
        </div>

        {#if posError}
            <div class="alert alert-warning">{posError}</div>
        {:else if posLoading}
            <div class="text-muted">Завантаження позицій...</div>
        {:else if posSummary}

            <!-- Зведення -->
            <div class="st-cards">
                <div class="st-card">
                    <div class="st-card-val">{posSummary.total}</div>
                    <div class="st-card-lbl">Всього угод</div>
                </div>
                <div class="st-card" style="border-color:#2e7d32">
                    <div class="st-card-val" style="color:#2e7d32">
                        {posSummary.wins}
                        <span class="st-card-pct">({posSummary.winrate}%)</span>
                    </div>
                    <div class="st-card-lbl">Прибуткових</div>
                </div>
                <div class="st-card" style="border-color:#c62828">
                    <div class="st-card-val" style="color:#c62828">{posSummary.losses}</div>
                    <div class="st-card-lbl">Збиткових</div>
                </div>
                <div class="st-card" style="border-color:{pnlColor(posSummary.total_pnl)}">
                    <div class="st-card-val" style="color:{pnlColor(posSummary.total_pnl)}">
                        {fmtPnl(posSummary.total_pnl)}
                    </div>
                    <div class="st-card-lbl">Загальний PnL, USDT</div>
                </div>
                <div class="st-card">
                    <div class="st-card-val" style="color:{pnlColor(posSummary.total > 0 ? posSummary.total_pnl / posSummary.total : 0)}">
                        {fmtPnl(posSummary.total > 0 ? posSummary.total_pnl / posSummary.total : 0)}
                    </div>
                    <div class="st-card-lbl">Сер. PnL, USDT</div>
                </div>
            </div>

            <!-- Фільтр таблиці на клієнті -->
            {#if positions.length > 0}
                <div class="pos-table-filter">
                    <input
                        class="pos-symbol-input"
                        type="text"
                        placeholder="Фільтр по символу..."
                        bind:value={posSymbolFilter}
                    />
                    <span class="pos-count">{filteredPositions.length} / {positions.length}</span>
                </div>

                <div class="table-responsive">
                    <table class="table table-sm table-hover st-table">
                        <thead>
                            <tr>
                                <th>Дата закриття</th>
                                <th>Символ</th>
                                <th>PnL (USDT)</th>
                                <th>Результат</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each filteredPositions as pos}
                                <tr class:pos-row-win={pos.pnl > 0} class:pos-row-loss={pos.pnl < 0}>
                                    <td class="st-date">{fmtTs(pos.time)}</td>
                                    <td><strong>{pos.symbol}</strong></td>
                                    <td>
                                        <span class="pos-pnl" style="color:{pnlColor(pos.pnl)}">
                                            {fmtPnl(pos.pnl)}
                                        </span>
                                    </td>
                                    <td>
                                        {#if pos.pnl > 0}
                                            <span class="pos-badge pos-badge--win">WIN</span>
                                        {:else if pos.pnl < 0}
                                            <span class="pos-badge pos-badge--loss">LOSS</span>
                                        {:else}
                                            <span class="pos-badge">BE</span>
                                        {/if}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {:else}
                <p class="text-muted mt-2">Закритих позицій за цей період не знайдено.</p>
            {/if}
        {/if}
    {/if}

    <!-- ══════════ ТАБ: АНАЛІЗ ПОМИЛОК ══════════ -->
    {#if activeTab === 'mistakes'}
        <div class="mk-controls">
            <div class="pos-periods">
                {#each (['7d','30d','90d','180d'] as MistakePeriod[]) as p}
                    <button
                        class="pos-period-btn"
                        class:pos-period-btn--active={mistakePeriod === p}
                        onclick={() => { mistakePeriod = p; loadLastMistakeReport(); }}
                    >{p}</button>
                {/each}
            </div>
            <button class="mk-run-btn" onclick={startMistakeAnalysis} disabled={mistakeLoading}>
                {#if mistakeLoading}
                    <span class="mk-spinner"></span> AI аналізує помилки...
                {:else}
                    ✦ Запустити аналіз
                {/if}
            </button>
        </div>

        {#if mistakeError}
            <div class="alert alert-warning">{mistakeError}</div>
        {/if}

        {#if mistakeReport?.created_at}
            <div class="mk-meta">
                Останній аналіз: {new Date(mistakeReport.created_at).toLocaleString('uk-UA')}
                · {mistakeReport.losses_count} збиткових угод
                · {mistakeReport.matched_count} зматчено з AI аналізами
                · {mistakeReport.ai_provider === 'gemini' ? 'Gemini' : 'Claude'}
            </div>
        {/if}

        {#if mistakeReport?.summary}
            <!-- Зведення -->
            <div class="mk-summary">{mistakeReport.summary}</div>

            {#if mistakeReport.ai_accuracy}
                <div class="mk-accuracy">
                    <span class="mk-block-label">Точність AI рекомендацій:</span>
                    {mistakeReport.ai_accuracy}
                </div>
            {/if}

            <div class="mk-grid">
                <!-- Шаблони помилок -->
                {#if mistakeReport.patterns.length}
                    <div class="mk-block">
                        <div class="mk-block-label">⚠ Шаблони помилок</div>
                        <ul class="mk-list mk-list--warn">
                            {#each mistakeReport.patterns as p}
                                <li>{p}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}

                <!-- Проблеми ризику -->
                {#if mistakeReport.risk_issues.length}
                    <div class="mk-block">
                        <div class="mk-block-label">✗ Проблеми управління ризиком</div>
                        <ul class="mk-list mk-list--loss">
                            {#each mistakeReport.risk_issues as r}
                                <li>{r}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}

                <!-- Рекомендації -->
                {#if mistakeReport.recommendations.length}
                    <div class="mk-block mk-block--full">
                        <div class="mk-block-label">✓ Рекомендації</div>
                        <ul class="mk-list mk-list--ok">
                            {#each mistakeReport.recommendations as r}
                                <li>{r}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}
            </div>

            {#if mistakeReport.worst_symbols.length}
                <div class="mk-worst">
                    <span class="mk-block-label">Найгірші монети: </span>
                    {#each mistakeReport.worst_symbols as s}
                        <span class="mk-sym-badge">{s}</span>
                    {/each}
                </div>
            {/if}
        {:else if !mistakeLoading}
            <p class="text-muted mt-2">
                Натисніть «Запустити аналіз» щоб AI проаналізував ваші збиткові угоди та порівняв з рекомендаціями.
            </p>
        {/if}
    {/if}

</div>

<style>
    .st-wrap { display: flex; flex-direction: column; gap: 16px; }

    /* ── Таби ────────────────────────────────────────── */
    .st-tabs {
        display: flex;
        gap: 0;
        border-bottom: 2px solid #e0e0e0;
    }
    .st-tab {
        padding: 7px 18px;
        border: none;
        background: none;
        cursor: pointer;
        font-size: 0.85rem;
        font-weight: 600;
        color: #888;
        border-bottom: 2px solid transparent;
        margin-bottom: -2px;
        transition: color 0.15s, border-color 0.15s;
    }
    .st-tab:hover { color: #333; }
    .st-tab--active { color: #1a56db; border-bottom-color: #1a56db; }

    /* ── Картки зведення ─────────────────────────────── */
    .st-cards {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
    }
    .st-card {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 10px 16px;
        min-width: 100px;
        text-align: center;
        background: #fafafa;
    }
    .st-card-val  { font-size: 1.4rem; font-weight: 700; }
    .st-card-pct  { font-size: 0.75rem; font-weight: 400; color: #888; }
    .st-card-lbl  { font-size: 0.72rem; color: #888; margin-top: 2px; }

    /* ── Топ символи ─────────────────────────────────── */
    .st-section-title { font-weight: 600; font-size: 0.85rem; color: #444; }
    .st-symbols { display: flex; gap: 6px; flex-wrap: wrap; }
    .st-sym-chip {
        padding: 3px 10px;
        border: 1px solid #ccc;
        border-radius: 20px;
        background: #fff;
        font-size: 0.75rem;
        cursor: pointer;
        transition: all 0.15s;
    }
    .st-sym-chip:hover, .st-sym-chip.active {
        background: #1976d2;
        color: #fff;
        border-color: #1976d2;
    }
    .st-sym-cnt {
        background: #e3f2fd;
        color: #1976d2;
        border-radius: 10px;
        padding: 0 5px;
        font-size: 0.7rem;
    }
    .st-sym-chip.active .st-sym-cnt {
        background: rgba(255,255,255,0.3);
        color: #fff;
    }

    /* ── Фільтр аналізів ─────────────────────────────── */
    .st-filter { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

    /* ── Таблиця ─────────────────────────────────────── */
    .st-table { font-size: 0.78rem; }
    .st-table th { font-size: 0.72rem; color: #666; font-weight: 600; white-space: nowrap; }
    .st-date { white-space: nowrap; color: #888; font-size: 0.72rem; }
    .st-summary { max-width: 260px; color: #555; font-size: 0.72rem; }
    .st-provider { font-size: 0.68rem; color: #aaa; font-weight: 500; }
    .st-provider.gemini { color: #1a73e8; }

    /* ── Позиції: контролі ───────────────────────────── */
    .pos-controls {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .pos-periods {
        display: flex;
        border: 1px solid #d0d0d0;
        border-radius: 6px;
        overflow: hidden;
    }
    .pos-period-btn {
        padding: 5px 12px;
        border: none;
        background: #fff;
        font-size: 0.8rem;
        font-weight: 600;
        color: #555;
        cursor: pointer;
        border-right: 1px solid #d0d0d0;
        transition: background 0.12s, color 0.12s;
    }
    .pos-period-btn:last-child { border-right: none; }
    .pos-period-btn:hover { background: #f0f4ff; color: #1a56db; }
    .pos-period-btn--active { background: #1a56db; color: #fff; }

    .pos-symbol-row {
        display: flex;
        gap: 6px;
        align-items: center;
    }

    .pos-symbol-input {
        padding: 4px 9px;
        font-size: 0.78rem;
        border: 1px solid #d0d0d0;
        border-radius: 5px;
        outline: none;
        width: 150px;
        transition: border-color 0.15s;
    }
    .pos-symbol-input:focus { border-color: #1a56db; }

    .pos-reload-btn {
        padding: 4px 10px;
        font-size: 0.78rem;
        font-weight: 600;
        border: 1px solid #d0d0d0;
        border-radius: 5px;
        background: #fff;
        cursor: pointer;
        transition: background 0.12s;
        white-space: nowrap;
    }
    .pos-reload-btn:hover:not(:disabled) { background: #f0f4ff; color: #1a56db; border-color: #1a56db; }
    .pos-reload-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ── Таблиця позицій ─────────────────────────────── */
    .pos-table-filter {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .pos-count {
        font-size: 0.75rem;
        color: #999;
    }

    .pos-pnl {
        font-weight: 700;
        font-size: 0.82rem;
    }

    .pos-row-win  td:first-child { border-left: 3px solid #2e7d32; }
    .pos-row-loss td:first-child { border-left: 3px solid #c62828; }

    .pos-badge {
        font-size: 0.65rem;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 3px;
        background: #f0f0f0;
        color: #888;
    }
    .pos-badge--win  { background: #e8f5e9; color: #2e7d32; }
    .pos-badge--loss { background: #ffebee; color: #c62828; }

    /* ── Аналіз помилок ──────────────────────────────── */
    .mk-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .mk-run-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 14px;
        font-size: 0.82rem;
        font-weight: 700;
        border: 1px solid #9c27b0;
        color: #9c27b0;
        background: transparent;
        border-radius: 5px;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        white-space: nowrap;
    }
    .mk-run-btn:hover:not(:disabled) { background: #9c27b0; color: #fff; }
    .mk-run-btn:disabled { opacity: 0.6; cursor: not-allowed; }

    .mk-spinner {
        display: inline-block;
        width: 10px;
        height: 10px;
        border: 2px solid #bbb;
        border-top-color: #9c27b0;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .mk-meta {
        font-size: 0.72rem;
        color: #999;
    }

    .mk-summary {
        padding: 10px 14px;
        background: #f8f4ff;
        border-left: 3px solid #9c27b0;
        border-radius: 0 6px 6px 0;
        font-size: 0.83rem;
        color: #333;
        line-height: 1.6;
    }

    .mk-accuracy {
        font-size: 0.8rem;
        color: #555;
        padding: 6px 0;
    }

    .mk-block-label {
        font-weight: 700;
        font-size: 0.78rem;
        color: #444;
        margin-bottom: 6px;
        display: block;
    }

    .mk-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .mk-block {
        background: #fafafa;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        padding: 10px 12px;
    }

    .mk-block--full {
        grid-column: 1 / -1;
    }

    .mk-list {
        margin: 0;
        padding-left: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .mk-list li { font-size: 0.78rem; line-height: 1.5; color: #333; }

    .mk-list--warn li { color: #b45309; }
    .mk-list--warn { list-style: none; padding-left: 0; }
    .mk-list--warn li::before { content: "⚠ "; }

    .mk-list--loss li { color: #c62828; }
    .mk-list--loss { list-style: none; padding-left: 0; }
    .mk-list--loss li::before { content: "✗ "; }

    .mk-list--ok li { color: #2e7d32; }
    .mk-list--ok { list-style: none; padding-left: 0; }
    .mk-list--ok li::before { content: "✓ "; }

    .mk-worst {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .mk-sym-badge {
        padding: 2px 8px;
        background: #ffebee;
        color: #c62828;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 700;
    }
</style>
